// i18n runtime built on i18next + react-i18next.
//  - 'ui' namespace: interface strings (locales/en/ui.json is the source; other locales translate it).
//  - 'content' namespace: structured content from content/facts.ts, keyed by src/i18n/content.ts.
// Translation files store, per key: text, sourceHash (of the English it was translated from), translatedAt,
// reviewedBy, reviewedAt. A translation whose sourceHash no longer matches the English is stale and dropped, so
// i18next falls back to English for it. Missing and stale keys are logged.
import { createInstance, type i18n as I18n, type TFunction } from 'i18next'
import { I18nextProvider, initReactI18next, useTranslation, Trans } from 'react-i18next'
import type { ReactElement, ReactNode } from 'react'
import enUi from '../../locales/en/ui.json'
import { contentEntries, contentKey, changeItemKey } from './content'
import { sourceHash } from './hash'
import { fmtStatutory, localeInfo, localizePath, money, type LocaleCode } from './locales'
import type { changelog } from '../../content/facts'
import { monthsUntil } from '../data'

export interface TranslationRecord { text: string; sourceHash: string; translatedAt: string; reviewedBy: string | null; reviewedAt: string | null }
export type TranslationFile = Record<string, TranslationRecord>
export interface LocaleFiles { ui?: TranslationFile; content?: TranslationFile }

export const englishUi: Record<string, string> = enUi
export const englishContent: Record<string, string> = Object.fromEntries(contentEntries.map(e => [e.key, e.text]))

export type Freshness = 'fresh' | 'stale' | 'missing'
export function freshness(en: Record<string, string>, tr: TranslationFile | undefined, key: string): Freshness {
  const r = tr?.[key]
  if (!r || !r.text) return 'missing'
  return r.sourceHash === sourceHash(en[key]) ? 'fresh' : 'stale'
}

/** Strings for a namespace with stale/missing keys removed (so English shows), plus what was removed. */
export function usableStrings(en: Record<string, string>, tr: TranslationFile | undefined) {
  const strings: Record<string, string> = {}
  const missing: string[] = [], stale: string[] = []
  for (const key of Object.keys(en)) {
    const f = freshness(en, tr, key)
    if (f === 'fresh') strings[key] = tr![key].text
    else (f === 'stale' ? stale : missing).push(key)
  }
  return { strings, missing, stale }
}

const logged = new Set<string>()
/** Creates a ready-to-use i18next instance synchronously (works for prerendering and before hydration). */
export function createI18n(locale: LocaleCode, files: LocaleFiles = {}): I18n {
  const resources: Record<string, Record<string, Record<string, string>>> = { en: { ui: englishUi, content: englishContent } }
  if (locale !== 'en') {
    const ui = usableStrings(englishUi, files.ui)
    const content = usableStrings(englishContent, files.content)
    resources[locale] = { ui: ui.strings, content: content.strings }
    const gaps = [...ui.missing.map(k => `ui:${k}`), ...content.missing.map(k => `content:${k}`)]
    const stale = [...ui.stale.map(k => `ui:${k}`), ...content.stale.map(k => `content:${k}`)]
    const sig = `${locale}:${gaps.length}:${stale.length}`
    if ((gaps.length || stale.length) && !logged.has(sig)) {
      logged.add(sig)
      console.warn(`[i18n] ${locale}: showing English for ${gaps.length} missing and ${stale.length} stale strings`, { missing: gaps.slice(0, 20), stale: stale.slice(0, 20) })
    }
  }
  const i18n = createInstance()
  i18n.use(initReactI18next).init({
    lng: locale, fallbackLng: 'en', ns: ['ui', 'content'], defaultNS: 'ui', resources,
    keySeparator: false, nsSeparator: false, initAsync: false,
    interpolation: { escapeValue: false }, // React escapes
    returnEmptyString: false,
  })
  return i18n
}

export function I18nProvider({ i18n, children }: { i18n: I18n; children: ReactNode }) {
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}

/** Translation helpers for components. */
export function useT() {
  const { t, i18n } = useTranslation('ui')
  const locale = i18n.language as LocaleCode
  return {
    t: t as TFunction<'ui'>,
    locale,
    info: localeInfo(locale),
    /** Translated text of a data field, falling back to the English in content/facts.ts. */
    tc: (obj: object, field = 'text') => {
      const english = String((obj as Record<string, unknown>)[field] ?? '')
      const key = contentKey(obj, field)
      return key ? i18n.t(key, { ns: 'content', defaultValue: english }) : english
    },
    tChange: (entry: (typeof changelog)[number], index: number) => i18n.t(changeItemKey(entry, index), { ns: 'content', defaultValue: entry.items[index] }),
    /** Locale-prefixed internal link ("/when" → "/zh/when"). */
    L: (href: string) => localizePath(href, locale),
    /** Statutory and content dates, written out in the locale. */
    date: (iso: string) => fmtStatutory(iso, locale),
    money,
    /** "18 MONTHS AWAY" etc. in the locale, computed from the build date. */
    until: (iso: string) => {
      const m = monthsUntil(iso)
      return m === null ? t('until.inEffect') : m < 1 ? t('until.underMonth') : m === 1 ? t('until.oneMonth') : t('until.months', { count: m })
    },
  }
}

/** <Trans> with the ui namespace and named tag components, e.g. T('banner.text', { b: <strong />, iras: <a href=… /> }). */
export function T({ k, values, components }: { k: string; values?: Record<string, unknown>; components?: Record<string, ReactElement> }) {
  return <Trans ns="ui" i18nKey={k} values={values} components={components} />
}
